import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Check } from 'lucide-react';
import type { Agent } from '@conversia/shared-types';
import * as agentsApi from '@/api/agents.api';
import * as installationApi from '@/api/installation.api';
import { ApiError } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/hooks/useToast';
import { useAuthStore } from '@/store/auth.store';
import { cn } from '@/lib/cn';

const schema = z.object({
  name: z.string().min(1, 'El nombre es obligatorio').max(120),
  description: z.string().max(500).optional(),
  systemPrompt: z.string().min(1, 'Las instrucciones son obligatorias'),
  status: z.enum(['ACTIVE', 'PAUSED', 'ARCHIVED']),
  engineType: z.enum(['api', 'cli']),
  // '' = sin modelo propio (usa el default de la instalación).
  model: z.string(),
  // Permiso fino del motor CLI (solo ADMIN; el server también lo exige).
  cliAllowed: z.boolean(),
});
type FormValues = z.infer<typeof schema>;

/**
 * El formulario va en TRES PASOS. Antes era un solo modal larguísimo donde el
 * nombre, el prompt, la whitelist, el modelo y el motor competían por la misma
 * atención, y quien creaba su primer agente no sabía qué era imprescindible.
 * Cada paso responde una pregunta: quién es, qué le decimos, cómo responde.
 *
 * Los campos de cada paso también sirven para validar de a poco: «Siguiente»
 * no avanza si lo de ESTE paso está mal (mismo zod, misma resolución).
 */
const PASOS = [
  {
    id: 'identidad',
    titulo: 'Identidad',
    ayuda: 'Cómo se llama y para qué está.',
    campos: ['name', 'description'] as const,
  },
  {
    id: 'instrucciones',
    titulo: 'Instrucciones',
    ayuda: 'Lo que el asistente tiene que saber y cómo debe hablar.',
    campos: ['systemPrompt'] as const,
  },
  {
    id: 'responde',
    titulo: 'Cómo responde',
    ayuda: 'A quién le contesta, con qué modelo y en qué estado queda.',
    campos: ['model', 'engineType', 'status'] as const,
  },
] as const;

export function AgentFormModal({
  open,
  onClose,
  agent,
}: {
  open: boolean;
  onClose: () => void;
  agent?: Agent | null;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const isEdit = Boolean(agent);
  const [paso, setPaso] = useState(0);

  // El selector de MOTOR solo existe en instalaciones internas de Silva Notion
  // (la vista CRM lo dice; la API además lo rechaza con 422 si no lo es).
  const { data: crmView } = useQuery({
    queryKey: ['installation', 'crm-view'],
    queryFn: installationApi.getCrmView,
    enabled: open,
  });
  const isInternal = crmView?.isInternal === true;
  const isAdmin = useAuthStore((s) => s.user?.role) === 'ADMIN';

  // Catálogo de modelos con descripciones llanas (configurable en el server).
  const { data: modelCatalog } = useQuery({
    queryKey: ['agents', 'models'],
    queryFn: agentsApi.getModelCatalog,
    enabled: open,
  });
  const modelos = modelCatalog?.modelos ?? [];

  // Whitelist de contactos: lista de strings gestionada fuera de RHF (tag input).
  const [whitelist, setWhitelist] = useState<string[]>([]);
  const [whitelistDraft, setWhitelistDraft] = useState('');

  const addWhitelistName = () => {
    const value = whitelistDraft.trim();
    if (!value) return;
    setWhitelist((prev) =>
      prev.some((n) => n.toLowerCase() === value.toLowerCase()) ? prev : [...prev, value],
    );
    setWhitelistDraft('');
  };
  const removeWhitelistName = (name: string) =>
    setWhitelist((prev) => prev.filter((n) => n !== name));

  const {
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      systemPrompt: '',
      status: 'ACTIVE',
      engineType: 'api',
      model: '',
      cliAllowed: false,
    },
  });
  const cliAllowed = watch('cliAllowed');
  const promptEscrito = watch('systemPrompt') ?? '';

  useEffect(() => {
    if (open) {
      reset({
        name: agent?.name ?? '',
        description: agent?.description ?? '',
        systemPrompt: agent?.systemPrompt ?? '',
        status: agent?.status ?? 'ACTIVE',
        engineType: agent?.engineType ?? 'api',
        model: agent?.model ?? '',
        cliAllowed: agent?.cliAllowed ?? false,
      });
      setWhitelist(Array.isArray(agent?.contactWhitelistJson) ? agent.contactWhitelistJson : []);
      setWhitelistDraft('');
      // Siempre se abre por el principio: editar o crear empieza en Identidad.
      setPaso(0);
    }
  }, [open, agent, reset]);

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      // El motor solo viaja cuando la instalación es interna (donde el
      // selector existe); en instalaciones de clientes ni se envía el campo.
      // Motor efectivo: sin permiso CLI no viaja 'cli' (el server igual lo
      // rechazaría con 422; la UI ya lo fuerza a 'api').
      const engineType = isInternal ? (values.cliAllowed ? values.engineType : 'api') : undefined;
      // cliAllowed solo lo puede mandar un ADMIN en instalación interna.
      const cliAllowedPayload = isInternal && isAdmin ? { cliAllowed: values.cliAllowed } : {};
      // '' = sin modelo propio → null (usa el default de la instalación).
      const model = values.model || null;
      if (isEdit && agent) {
        return agentsApi.updateAgent(agent.id, {
          name: values.name,
          description: values.description || undefined,
          systemPrompt: values.systemPrompt,
          status: values.status,
          contactWhitelist: whitelist,
          model,
          ...(engineType ? { engineType } : {}),
          ...cliAllowedPayload,
        });
      }
      return agentsApi.createAgent({
        name: values.name,
        description: values.description || undefined,
        systemPrompt: values.systemPrompt,
        contactWhitelist: whitelist,
        model,
        ...(engineType ? { engineType } : {}),
        ...cliAllowedPayload,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['agents'] });
      toast.success(isEdit ? 'Agente actualizado' : 'Agente creado');
      onClose();
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : 'No se pudo guardar el agente';
      setError('root', { message });
    },
  });

  const onSubmit = handleSubmit((values) => mutation.mutate(values));

  /** Avanza solo si lo de ESTE paso está bien: el error se ve donde se cometió. */
  async function siguiente() {
    const ok = await trigger([...PASOS[paso]!.campos]);
    if (ok) setPaso((p) => Math.min(p + 1, PASOS.length - 1));
  }

  /** Ir a un paso ya visto es libre; hacia adelante, validando. */
  async function irAPaso(destino: number) {
    if (destino <= paso) {
      setPaso(destino);
      return;
    }
    const ok = await trigger([...PASOS[paso]!.campos]);
    if (ok) setPaso(destino);
  }

  const esUltimo = paso === PASOS.length - 1;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={isEdit ? 'Editar agente' : 'Nuevo agente'}
      description={PASOS[paso]!.ayuda}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">
            Cancelar
          </Button>
          {paso > 0 && (
            <Button variant="outline" type="button" onClick={() => setPaso((p) => p - 1)}>
              Atrás
            </Button>
          )}
          {esUltimo ? (
            <Button onClick={onSubmit} loading={mutation.isPending}>
              {isEdit ? 'Guardar cambios' : 'Crear agente'}
            </Button>
          ) : (
            <Button type="button" onClick={() => void siguiente()}>
              Siguiente
            </Button>
          )}
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <IndicadorDePasos paso={paso} onIr={(i) => void irAPaso(i)} />

        {paso === 0 && (
          <div className="space-y-4">
            <Input
              label="Nombre"
              placeholder="Asistente de ventas"
              error={errors.name?.message}
              {...register('name')}
            />
            <Input
              label="Descripción"
              placeholder="Atiende consultas de productos y precios"
              error={errors.description?.message}
              {...register('description')}
            />
          </div>
        )}

        {paso === 1 && (
          <div className="space-y-2">
            <Textarea
              label="Instrucciones del asistente"
              rows={14}
              placeholder="Eres un asistente de ventas amable…"
              error={errors.systemPrompt?.message}
              // Monoespaciada: estas instrucciones se leen y se corrigen línea
              // por línea, y con ancho fijo se ve dónde termina cada regla.
              className="font-mono text-sm"
              {...register('systemPrompt')}
            />
            <p className="flex items-center justify-between text-xs text-slate-400">
              <span>
                Escríbelas como se las explicarías a un empleado nuevo: qué vende, cómo trata al
                cliente, qué no debe prometer.
              </span>
              <span className="shrink-0 tabular-nums">
                {promptEscrito.length.toLocaleString('es-DO')} caracteres
              </span>
            </p>
          </div>
        )}

        {paso === 2 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                Solo contesta a estos números o nombres
              </label>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Vacía: contesta a todos. Con algo cargado, solo a quien coincida. Números con o sin
                el 1 (809-555-1234 o 18095551234). Ojo: los contactos que WhatsApp muestra como
                «LID» no traen número y solo coinciden por nombre.
              </p>
              <div className="flex flex-wrap gap-1.5">
                {whitelist.map((name) => (
                  <span
                    key={name}
                    className="inline-flex items-center gap-1 rounded-full bg-brand-100 px-2.5 py-1 text-xs font-medium text-brand-700 dark:bg-brand-900/40 dark:text-brand-300"
                  >
                    {name}
                    <button
                      type="button"
                      onClick={() => removeWhitelistName(name)}
                      className="rounded-full p-0.5 hover:bg-brand-200 dark:hover:bg-brand-800"
                      aria-label={`Quitar ${name}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex items-end gap-2">
                <Input
                  placeholder="Número o nombre (ej: 809-555-1234)"
                  value={whitelistDraft}
                  onChange={(e) => setWhitelistDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addWhitelistName();
                    }
                  }}
                />
                <Button type="button" variant="outline" onClick={addWhitelistName}>
                  Agregar
                </Button>
              </div>
              <p className="text-xs text-slate-400">
                💡 Si la lista está vacía, el agente responde a todos. Si tiene nombres, sólo
                responde a contactos cuyo nombre contenga alguno de estos textos (sin distinguir
                mayúsculas).
              </p>
            </div>

            <div>
              <Select
                label="Modelo de IA (motor API)"
                error={errors.model?.message}
                options={[
                  { value: '', label: 'Default de la instalación (recomendado si no sabes cuál)' },
                  ...modelos.map((m) => ({
                    value: m.id,
                    label: `${m.nombre} — ${m.descripcion}`,
                  })),
                ]}
                {...register('model')}
              />
              <p className="mt-1 text-xs text-slate-400">
                Con qué "cerebro" responde este agente. Cambiarlo no toca sus instrucciones ni su
                comportamiento configurado, solo el modelo que lo ejecuta.
              </p>
            </div>

            {isInternal && (
              <div className="space-y-2 rounded-lg border border-surface-border p-3 dark:border-slate-700">
                {isAdmin && (
                  <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-200">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={cliAllowed}
                      onChange={(e) => {
                        setValue('cliAllowed', e.target.checked);
                        // Sin permiso no hay motor CLI que mantener.
                        if (!e.target.checked) setValue('engineType', 'api');
                      }}
                    />
                    <span>
                      <span className="font-medium">Permitir motor CLI</span> — usa la suscripción
                      Max configurada, no la API. Solo un administrador puede cambiarlo.
                    </span>
                  </label>
                )}
                <Select
                  label="Motor de IA"
                  error={errors.engineType?.message}
                  disabled={!cliAllowed}
                  options={[
                    { value: 'api', label: 'API Anthropic (BYOK) — estándar' },
                    { value: 'cli', label: 'CLI — suscripción Max vía VPS (interno)' },
                  ]}
                  {...register('engineType')}
                />
                <p className="text-xs text-slate-400">
                  {cliAllowed
                    ? 'El motor CLI usa la suscripción del equipo en el VPS interno (requiere el vps-agent configurado).'
                    : "Para elegir el motor CLI, primero activa 'Permitir motor CLI' (solo admin)."}
                </p>
              </div>
            )}

            {isEdit && (
              <Select
                label="Estado"
                error={errors.status?.message}
                options={[
                  { value: 'ACTIVE', label: 'Activo' },
                  { value: 'PAUSED', label: 'Pausado' },
                  { value: 'ARCHIVED', label: 'Archivado' },
                ]}
                {...register('status')}
              />
            )}
          </div>
        )}

        {errors.root && <p className="text-sm text-danger">{errors.root.message}</p>}
      </form>
    </Modal>
  );
}

/** Dónde estoy y cuánto falta: los tres pasos, siempre a la vista. */
function IndicadorDePasos({ paso, onIr }: { paso: number; onIr: (i: number) => void }) {
  return (
    <ol className="flex items-center gap-2">
      {PASOS.map((p, i) => {
        const hecho = i < paso;
        const actual = i === paso;
        return (
          <li key={p.id} className="flex flex-1 items-center gap-2">
            <button
              type="button"
              onClick={() => onIr(i)}
              className="flex min-w-0 items-center gap-2 text-left"
            >
              <span
                className={cn(
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                  actual
                    ? 'bg-brand-600 text-white'
                    : hecho
                      ? 'bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300'
                      : 'bg-slate-100 text-slate-400 dark:bg-slate-800',
                )}
              >
                {hecho ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </span>
              <span
                className={cn(
                  'truncate text-xs font-medium',
                  actual ? 'text-slate-900 dark:text-slate-100' : 'text-slate-400',
                )}
              >
                {p.titulo}
              </span>
            </button>
            {i < PASOS.length - 1 && (
              <span className="h-px flex-1 bg-surface-border dark:bg-slate-700" />
            )}
          </li>
        );
      })}
    </ol>
  );
}
